import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Customer {
  id: string;
  restaurant_id: string;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  address_complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  notes: string | null;
  accept_marketing: boolean;
  source: string;
  total_orders: number;
  total_spent: number;
  last_order_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CustomerFormData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  address_complement?: string;
  neighborhood?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  notes?: string;
  accept_marketing?: boolean;
}

export function useCustomers(restaurantId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: customers = [], isLoading, error } = useQuery({
    queryKey: ["customers", restaurantId],
    queryFn: async () => {
      if (!restaurantId) return [];

      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("name");

      if (error) throw error;
      return data as Customer[];
    },
    enabled: !!restaurantId,
  });

  const createMutation = useMutation({
    mutationFn: async (customer: CustomerFormData) => {
      if (!restaurantId) throw new Error("Restaurant ID is required");

      const { data, error } = await supabase
        .from("customers")
        .insert({
          restaurant_id: restaurantId,
          ...customer,
          source: "manual",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", restaurantId] });
      toast.success("Cliente cadastrado com sucesso!");
    },
    onError: (error: Error) => {
      if (error.message.includes("duplicate")) {
        toast.error("Já existe um cliente com este telefone");
      } else {
        toast.error("Erro ao cadastrar cliente");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...customer }: CustomerFormData & { id: string }) => {
      const { data, error } = await supabase
        .from("customers")
        .update(customer)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", restaurantId] });
      toast.success("Cliente atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar cliente");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("customers").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customers", restaurantId] });
      toast.success("Cliente excluído com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao excluir cliente");
    },
  });

  // Import customers from ratings
  const importFromRatingsMutation = useMutation({
    mutationFn: async () => {
      if (!restaurantId) throw new Error("Restaurant ID is required");

      // Get ratings with customer data that haven't been imported yet
      const { data: ratings, error: ratingsError } = await supabase
        .from("customer_ratings")
        .select("customer_name, customer_email, customer_phone, accept_marketing")
        .eq("restaurant_id", restaurantId)
        .not("customer_phone", "is", null);

      if (ratingsError) throw ratingsError;

      // Filter unique customers by phone
      const uniqueCustomers = new Map<string, typeof ratings[0]>();
      for (const rating of ratings) {
        if (rating.customer_phone && !uniqueCustomers.has(rating.customer_phone)) {
          uniqueCustomers.set(rating.customer_phone, rating);
        }
      }

      let imported = 0;
      for (const [phone, rating] of uniqueCustomers) {
        // Check if customer already exists
        const { data: existing } = await supabase
          .from("customers")
          .select("id")
          .eq("restaurant_id", restaurantId)
          .eq("phone", phone)
          .single();

        if (!existing && rating.customer_name) {
          const { error } = await supabase.from("customers").insert({
            restaurant_id: restaurantId,
            name: rating.customer_name,
            email: rating.customer_email,
            phone: rating.customer_phone,
            accept_marketing: rating.accept_marketing || false,
            source: "rating",
          });

          if (!error) imported++;
        }
      }

      return imported;
    },
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["customers", restaurantId] });
      if (count > 0) {
        toast.success(`${count} cliente(s) importado(s) das avaliações!`);
      } else {
        toast.info("Nenhum cliente novo para importar");
      }
    },
    onError: () => {
      toast.error("Erro ao importar clientes");
    },
  });

  return {
    customers,
    isLoading,
    error,
    createCustomer: createMutation.mutateAsync,
    updateCustomer: updateMutation.mutateAsync,
    deleteCustomer: deleteMutation.mutateAsync,
    importFromRatings: importFromRatingsMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isImporting: importFromRatingsMutation.isPending,
  };
}
